import type { AccountStats } from '../lib/api';
import { formatBytes } from '../lib/format';

const RADIUS = 52;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export interface BatchProgress {
  done: number;
  total: number;
}

interface Props {
  stats: AccountStats;
  /** Set while a conversion is running, so the ring reports live progress. */
  batch: BatchProgress | null;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/**
 * One ring doing two jobs. While files are converting it counts them, which is
 * the thing people watch; the rest of the time it shows the share of space the
 * conversions gave back. The caption always says which of the two it is.
 */
export function Stats({ stats, batch }: Props) {
  const converting = batch !== null && batch.total > 0;

  const delta = stats.originalBytes - stats.outputBytes;
  const grew = delta < 0;
  const savedFraction = stats.originalBytes > 0 && !grew ? delta / stats.originalBytes : 0;

  const fraction = converting ? Math.min(1, batch.done / batch.total) : savedFraction;
  const reading = converting ? batch.done + '/' + batch.total : Math.round(savedFraction * 100) + '%';
  const caption = converting ? 'converting' : 'space saved';

  const label = converting
    ? 'Converted ' + batch.done + ' of ' + batch.total + ' files so far'
    : Math.round(savedFraction * 100) + ' percent of the original size saved';

  // PNG output is routinely larger than the HEIC that produced it, so say that
  // plainly rather than reporting a saving of zero.
  const outputWidth = stats.originalBytes > 0
    ? Math.min(100, (stats.outputBytes / stats.originalBytes) * 100)
    : 0;

  return (
    <section className="card stats-card">
      <div className="gauge" role="img" aria-label={label}>
        <svg viewBox="0 0 120 120" aria-hidden="true">
          <circle className="gauge-track" cx="60" cy="60" r={RADIUS} />
          <circle
            className={'gauge-value' + (converting ? ' is-busy' : '')}
            cx="60"
            cy="60"
            r={RADIUS}
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={CIRCUMFERENCE * (1 - fraction)}
          />
        </svg>
        <div className="gauge-face">
          <strong>{reading}</strong>
          <span>{caption}</span>
        </div>
      </div>

      <div className="stats-body">
        <div className="stats-figures">
          <div className="figure">
            <strong>{stats.converted.toLocaleString()}</strong>
            <span>{plural(stats.converted, 'photo converted', 'photos converted')}</span>
          </div>
          <div className="figure">
            <strong className={grew ? undefined : 'saving'}>{formatBytes(Math.abs(delta))}</strong>
            <span>{grew ? 'larger than the originals' : 'space saved'}</span>
          </div>
          {stats.failed > 0 ? (
            <div className="figure">
              <strong>{stats.failed.toLocaleString()}</strong>
              <span>{plural(stats.failed, 'file failed', 'files failed')}</span>
            </div>
          ) : null}
        </div>

        <div className="size-bars">
          <div className="size-row">
            <span className="size-label">Original</span>
            <span className="size-track">
              <span className="size-fill is-original" style={{ width: '100%' }} />
            </span>
            <span className="size-value">{formatBytes(stats.originalBytes)}</span>
          </div>
          <div className="size-row">
            <span className="size-label">Converted</span>
            <span className="size-track">
              <span className="size-fill is-output" style={{ width: outputWidth + '%' }} />
            </span>
            <span className="size-value">{formatBytes(stats.outputBytes)}</span>
          </div>
        </div>

        <p className="stats-note">Totals for your account. Files themselves are still deleted after 24 hours.</p>
      </div>
    </section>
  );
}
